import { useState, useEffect, useCallback, useRef } from "react";
import { getUnifiedBusinessDashboard } from "../../api/endpoints/dashboard/dashboardApi";
import { BusinessReviewIssue, BusinessReviewMeta } from "./useBusinessReviewIssues";

// Define interfaces for all the dashboard data
export interface DashboardMetrics {
  totalReports: {
    current: number;
    changePercent: number | null;
  };
  activeIssues?: {
    current: number;
    changePercent: number | null;
  };
  totalPrograms: {
    current: number;
    changePercent: number | null;
  };
  resolvedIssues: {
    current: number;
    changePercent: number | null;
  };
  retestsRemaining: number;
  awaitingReview?: number;
}

export interface SeverityCount {
  total: number;
  businessResolved: number;
}

export interface SeverityOverview {
  critical: SeverityCount;
  high: SeverityCount;
  medium: SeverityCount;
  low: SeverityCount;
}

export interface ProgramSeverity {
  program_id: number;
  name: string;
  description?: string;
  severityOverview?: SeverityOverview;
}

export interface TimeSeriesPoint {
  critical: number;
  high: number;
  medium: number;
  low: number;
  [key: string]: any; // For additional properties like date, month, etc.
}

export interface ReportsTrend {
  weekly: TimeSeriesPoint[];
  monthly: TimeSeriesPoint[];
  quarterly: TimeSeriesPoint[];
  yearly: TimeSeriesPoint[];
}

export interface StatusCounts {
  pending: number;
  inProgress: number;
  completed: number;
}

export interface PassRate {
  percentage: number;
  completed: number;
  changePercent: number;
}

export interface ResultsSummary {
  passed: number;
  failed: number;
  actionRequired: number;
}

export interface AverageDuration {
  days: number;
  changePercent: number;
}

export interface RetestSummary {
  statusCounts: StatusCounts;
  passRate: PassRate;
  resultsSummary: ResultsSummary;
  latestCompletion: string | null;
  averageDuration: AverageDuration;
}

export interface RetestAction {
  retest_id: number;
  report_id: number;
  title: string;
  severity_category: string;
  scope: string | null;
  status: string;
  submitted_date: string | null;
  updated_date: string;
  days_taken: number | null;
}

export interface UnifiedDashboardData {
  metrics: DashboardMetrics;
  severityOverview: {
    total: SeverityOverview;
    programs: ProgramSeverity[];
  };
  reportsTrend: ReportsTrend;
  businessReviewReports: BusinessReviewIssue[];
  businessReviewMeta?: BusinessReviewMeta;
  retestSummary: RetestSummary;
  retestActions: RetestAction[];
  hasActiveSubscription?: boolean;
  activeSubscription?: { startDate: string; endDate: string } | null;
  subscriptionDates?: { startDate: string; endDate: string } | null;
  firstProgramYear?: number;
}

// Module-level in-memory cache for instant tab-switching (0ms perceived latency)
interface UnifiedCacheEntry {
  data: UnifiedDashboardData;
  timestamp: number;
}
const unifiedDashboardCache = new Map<string, UnifiedCacheEntry>();
const CACHE_FRESH_MS = 30_000;

function getCacheKey(
  includeArchived?: boolean,
  subscriptionOnly?: boolean,
  selectedYear?: number | null
): string {
  return `${includeArchived ?? false}|${subscriptionOnly ?? false}|${selectedYear ?? ""}`;
}

/**
 * Hook to fetch unified business dashboard data
 * @param includeArchived Whether to include archived reports in the dashboard data
 * @param subscriptionOnly Whether to filter data for the active subscription only
 * @returns All dashboard data with loading and error states
 */
export default function useUnifiedBusinessDashboard(includeArchived?: boolean, subscriptionOnly?: boolean, selectedYear?: number | null) {
  const cacheKey = getCacheKey(includeArchived, subscriptionOnly, selectedYear);
  const cached = unifiedDashboardCache.get(cacheKey);

  const [data, setData] = useState<UnifiedDashboardData | null>(cached ? cached.data : null);
  const [loading, setLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<string | null>(null);
  const latestFetchRef = useRef<(force?: boolean) => void>(() => {});

  useEffect(() => {
    let active = true;
    const currentCached = unifiedDashboardCache.get(cacheKey);

    if (currentCached) {
      setData(currentCached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchData = async (force: boolean = false) => {
      const entry = unifiedDashboardCache.get(cacheKey);
      if (!force && entry && Date.now() - entry.timestamp < CACHE_FRESH_MS) {
        if (active) {
          setData(entry.data);
          setLoading(false);
        }
        return;
      }

      try {
        if (!entry) {
          setLoading(true);
        }
        setError(null);

        const response = await getUnifiedBusinessDashboard(includeArchived, subscriptionOnly, selectedYear);

        if (!active) return;

        // Validate response has the expected shape
        if (
          response &&
          typeof response === 'object' &&
          'success' in response &&
          response.success === true &&
          'data' in response
        ) {
          const dashboardData = response.data;

          if (
            dashboardData &&
            typeof dashboardData === 'object' &&
            'metrics' in dashboardData
          ) {
            // Attach subscription metadata from the root response to the data object
            const enrichedData = {
              ...dashboardData,
              hasActiveSubscription: response.hasActiveSubscription,
              activeSubscription: response.activeSubscription,
              subscriptionDates: response.subscriptionDates,
              firstProgramYear: response.firstProgramYear
            } as UnifiedDashboardData;

            unifiedDashboardCache.set(cacheKey, {
              data: enrichedData,
              timestamp: Date.now()
            });

            setData(enrichedData);
          } else {
            console.error("Invalid dashboard data structure:", dashboardData);
            setError("The dashboard data format is invalid");
          }
        } else {
          console.error("Unexpected dashboard response format:", response);
          setError("The dashboard API response format is invalid");
        }
      } catch (err) {
        if (!active) return;
        console.error("Error fetching unified dashboard data:", err);
        setError("Failed to load dashboard data");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    latestFetchRef.current = (force = true) => fetchData(force);
    fetchData();

    return () => {
      active = false;
    };
  }, [cacheKey, includeArchived, subscriptionOnly, selectedYear]);

  const refetch = useCallback(() => {
    latestFetchRef.current(true);
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch,
    
    // Convenience getters
    metrics: data?.metrics,
    severityOverview: data?.severityOverview,
    reportsTrend: data?.reportsTrend,
    businessReviewReports: data?.businessReviewReports,
    retestSummary: data?.retestSummary,
    retestActions: data?.retestActions,
    hasActiveSubscription: data?.hasActiveSubscription,
    activeSubscription: data?.activeSubscription,
    subscriptionDates: data?.subscriptionDates,
    firstProgramYear: data?.firstProgramYear,
    
    // Meta information
    businessReviewMeta: data?.businessReviewMeta || 
      // Fallback to creating meta info from available data
      (data ? { 
        // Use metrics.totalReports.current as the source of truth for total report count if available
        totalCount: data?.metrics?.totalReports?.current || 
                   (data?.businessReviewReports ? data.businessReviewReports.length : 0),
        // If we have both metrics and reports list, we can determine if there are more reports
        hasMore: data?.metrics?.totalReports?.current && data?.businessReviewReports ? 
                data?.metrics?.totalReports?.current > data.businessReviewReports.length : 
                false
      } : null)
  };
} 